import React, { useMemo } from "react";
import {
  Paper, List, ListItem, ListItemAvatar, Avatar,
  ListItemText, ListItemSecondaryAction, Box, Typography, Chip, Divider, Button
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const UsersSection = ({ title, items, onEdit }) => {
  const { t } = useTranslation();

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2, mb: 1 }}>
        <Chip label={title} variant="outlined" />
        <Chip label={items.length} variant="outlined" size="small" />
      </Box>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <List dense>
          {items.length ? (
            items.map((up) => {
              const u = up.user || {};
              return (
                <ListItem key={up.id} divider>
                  <ListItemAvatar>
                    <Avatar>{(u.username || "?")[0]?.toUpperCase()}</Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {u.username}
                        </Typography>
                        <Chip label={up.role} size="small" variant="outlined" />
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {`${u.first_name || ""} ${u.last_name || ""}`.trim() || "—"}
                        {u.email ? ` • ${u.email}` : ""}
                        {up.number ? ` • ${t("usersList.user.number", "number")}: ${up.number}` : ""}
                        {/* --> ADDED EXTERNAL ID HERE <-- */}
                        {up.external_id ? ` • ${t("usersList.user.externalId", "Ext. ID")}: ${up.external_id}` : ""}
                      </Typography>
                    }
                  />

                  <ListItemSecondaryAction>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => onEdit(up)}
                    >
                      {t("usersList.actions.edit", "Edit")}
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })
          ) : (
            <Box p={2}>
              <Typography variant="body2" color="text.secondary">
                {t("usersList.empty", "No users.")}
              </Typography>
            </Box>
          )}
        </List>
      </Paper>
    </>
  );
};

const UsersList = ({ users, onEdit, roles }) => {
  const roleOrder = useMemo(
    () => Object.keys(roles).sort((a, b) => roles[a].order - roles[b].order),
    [roles]
  );

  const grouped = useMemo(() => {
    const g = {};
    roleOrder.forEach((r) => (g[r] = []));

    users.forEach((up) => {
      const role = roles[up.role] ? up.role : "user"; // fallback
      g[role].push(up);
    });

    const sortByUsername = (a, b) =>
      (a.user?.username || "").localeCompare(b.user?.username || "");

    roleOrder.forEach((r) => g[r].sort(sortByUsername));
    return g;
  }, [users, roles, roleOrder]);

  return (
    <>
      {roleOrder.map((role, idx) => (
        <React.Fragment key={role}>
          <UsersSection title={roles[role].label} items={grouped[role]} onEdit={onEdit} />
          {idx < roleOrder.length - 1 && <Divider sx={{ my: 1 }} />}
        </React.Fragment>
      ))}
    </>
  );
};

export default UsersList;